import { css } from "@emotion/react";
import { Button, CircularProgress, Theme, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { GraphQLClient, gql } from "graphql-request";
import ErrorPage from "next/error";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";

import CreateAccountLead from "~/components/common/CreateAccountLead";
import PageHead from "~/components/common/PageHead";
import { mediaQueryIsDesktop, mediaQueryIsMobile } from "~/lib/client/design";
import { useGraphqlClientWithAuth } from "~/lib/client/hooks/useGraphqlClient";
import { Layout } from "~/lib/client/layout";
import environment from "~/lib/environment";
import { useAppContext } from "~/pages/_app";

type FrontendPurchaseStatus =
  | "notPurchased"
  | "purchased"
  | "taken"
  | "loading"
  | "error"
  | "verifying"
  | "questRequired"
  | null;

const EXAMS_WITH_PERFECT_COUNT: string[] = [
  "madososhi-advanced",
  "domundiexam-gen12",
  "domundiexam-gen345",
];

const ExamPurchasePage = () => {
  const router = useRouter();
  const theme = useTheme();
  const { currentUser } = useAppContext();
  const client = useGraphqlClientWithAuth();
  const { id, examId, orderId } = router.query;
  const examIdStr =
    typeof examId === "string"
      ? examId
      : Array.isArray(examId)
        ? examId[0]
        : "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNotFound, setShowNotFound] = useState(false);
  const [purchaseStatus, setPurchaseStatus] =
    useState<FrontendPurchaseStatus>("loading");
  const [examDetails, setExamDetails] = useState<{
    id: string;
    name: string;
    description: string;
    price: number;
    examMakerId: string;
    level: Number | null;
    passingScore: Number;
    examAvailableFrom?: string;
    examAvailableTo?: string;
    isFree?: boolean;
  } | null>(null);
  const [isVerifyingPurchase, setIsVerifyingPurchase] = useState(false);
  const verificationAttempt = useRef(0);
  const verificationTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const [perfectCount, setPerfectCount] = useState<number | null>(null);
  const [perfectLoading, setPerfectLoading] = useState(false);
  const [perfectFetchError, setPerfectFetchError] = useState(false);

  const fetchExamDetails = useCallback(async () => {
    if (!client || !examId) return;
    try {
      const response = await client.GetExamDetails({
        internalId: examId as string,
      });
      if (response?.getExamDetails) {
        const details = {
          id: response.getExamDetails.id,
          name: response.getExamDetails.name,
          description: response.getExamDetails.description,
          price: response.getExamDetails.price,
          examMakerId: response.getExamDetails.examMakerId,
          level: response.getExamDetails.level ?? null,
          passingScore: response.getExamDetails.passingScore,
          examAvailableFrom: response.getExamDetails.examAvailableFrom,
          examAvailableTo: response.getExamDetails.examAvailableTo,
          isFree: response.getExamDetails.isFree,
        };
        setExamDetails(details);
        return details;
      } else {
        throw new Error("検定情報の取得に失敗しました");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "検定情報の取得エラー");
      setPurchaseStatus("error");
    }
  }, [client, examId]);

  const checkPurchaseStatus =
    useCallback(async (): Promise<FrontendPurchaseStatus> => {
      if (!client || !examId) return "error";

      try {
        const response = await client.validateExamAccess({
          internalId: examId as string,
        });

        const validation = response?.validateExamAccess;

        if (!validation) {
          throw new Error("購入状態確認失敗(No validation data)");
        }

        if (validation.canAccess === true) {
          return "purchased";
        }
        switch (validation.status) {
          case "PURCHASED":
            return "purchased";
          case "TAKEN":
            return "taken";
          case "NOT_PURCHASED":
            return "notPurchased";
          case "QUEST_REQUIRED":
            setError(
              "この検定を受験するには、特定のクエストの実行が必要です。"
            );
            return "questRequired";
          case "AUTH_REQUIRED":
            setError("ログインが必要です");
            return "error";
          case "EXAM_NOT_FOUND":
            setShowNotFound(true);
            return null;
          default:
            setError("購入状態確認エラー");
            return "error";
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "購入状態確認中にエラー");
        return "error";
      }
    }, [client, examId]);

  useEffect(() => {
    const currentOrderId = Array.isArray(orderId) ? orderId[0] : orderId;

    if (!router.isReady || !client || !examId || currentUser === undefined) {
      return;
    }
    if (currentUser === null) {
      setLoading(false);
      setPurchaseStatus(null);
      return;
    }

    if (purchaseStatus !== "loading" && purchaseStatus !== null) {
      return;
    }

    const justRedirected = !!currentOrderId;

    if (currentOrderId) {
      setIsVerifyingPurchase(true);
      setLoading(true);
      setError(null);
      setPurchaseStatus("verifying");
      verificationAttempt.current = 0;
    } else {
      // --- 通常フローの場合 (初回ロード) ---
      setIsVerifyingPurchase(false);
      setLoading(true);
      setError(null);

      fetchExamDetails()
        .then(async (details) => {
          // 無料検定の場合は購入ステータスチェックをスキップ
          if (details?.isFree) {
            setPurchaseStatus("notPurchased");
          } else {
            const statusResult = await checkPurchaseStatus();
            setPurchaseStatus(statusResult ?? "error");
          }
        })
        .catch((err) => {
          setError(err instanceof Error ? err.message : "データ読み込み失敗");
          setPurchaseStatus("error");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    router.isReady,
    client,
    examId,
    currentUser,
    orderId,
    purchaseStatus,
    fetchExamDetails,
    checkPurchaseStatus,
    isVerifyingPurchase,
  ]);

  useEffect(() => {
    if (
      isVerifyingPurchase ||
      (purchaseStatus !== "loading" && purchaseStatus !== null) ||
      !client ||
      !currentUser
    ) {
      return;
    }

    setLoading(true);
    setError(null);

    fetchExamDetails()
      .then(async (details) => {
        // 無料検定の場合は購入ステータスチェックをスキップ
        if (details?.isFree) {
          setPurchaseStatus("notPurchased");
        } else {
          const statusResult = await checkPurchaseStatus();
          setPurchaseStatus(statusResult ?? "error");
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "データ取得エラー");
        setPurchaseStatus("error");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    purchaseStatus,
    client,
    currentUser,
    fetchExamDetails,
    checkPurchaseStatus,
    isVerifyingPurchase,
  ]);

  // --- Effect 3: 購入検証 & リトライ処理 ---
  useEffect(() => {
    if (!isVerifyingPurchase) {
      if (verificationTimeoutId.current) {
        clearTimeout(verificationTimeoutId.current);
        verificationTimeoutId.current = null;
      }
      return;
    }

    const MAX_ATTEMPTS = 3;
    const RETRY_DELAY = 3000;

    const verify = async () => {
      verificationAttempt.current += 1;
      setError(null);

      try {
        const statusResult = await checkPurchaseStatus();

        if (
          statusResult === "purchased" ||
          statusResult === "taken" ||
          statusResult === "error" ||
          statusResult === "questRequired"
        ) {
          setPurchaseStatus(statusResult);
          setIsVerifyingPurchase(false);
          await fetchExamDetails();
          setLoading(false);
        } else if (
          statusResult === "notPurchased" &&
          verificationAttempt.current < MAX_ATTEMPTS
        ) {
          verificationTimeoutId.current = setTimeout(verify, RETRY_DELAY);
        } else {
          setError("購入情報を確認できませんでした。再読み込みしてください。");
          setPurchaseStatus("error");
          setIsVerifyingPurchase(false);
          setLoading(false);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "購入確認中に予期せぬエラー"
        );
        setPurchaseStatus("error");
        setIsVerifyingPurchase(false);
        setLoading(false);
      }
    };

    verify();

    return () => {
      if (verificationTimeoutId.current) {
        clearTimeout(verificationTimeoutId.current);
        verificationTimeoutId.current = null;
      }
    };
  }, [isVerifyingPurchase, checkPurchaseStatus, fetchExamDetails]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (!EXAMS_WITH_PERFECT_COUNT.includes(examIdStr)) {
      setPerfectCount(null);
      setPerfectLoading(false);
      setPerfectFetchError(false);
      return;
    }

    let isMounted = true;
    let timer: ReturnType<typeof setInterval> | null = null;

    const fetchPerfectCount = async () => {
      try {
        if (!isMounted) return;
        const host = environment().backendHost;
        if (!host) {
          throw new Error("backendHost is not configured");
        }

        const graphqlClient = new GraphQLClient(`${host}/graphql`);
        const query = gql`
          query PerfectCount($internalId: String!) {
            examRanking(internalId: $internalId) {
              perfectScoreCount
            }
          }
        `;
        type PerfectCountQueryResponse = {
          examRanking?: { perfectScoreCount?: number | null } | null;
        };
        const res = await graphqlClient.request<PerfectCountQueryResponse>(
          query,
          {
            internalId: examIdStr,
          }
        );
        if (!isMounted) return;

        const count = res.examRanking?.perfectScoreCount ?? null;
        if (typeof count === "number") {
          setPerfectCount(count);
          setPerfectFetchError(false);
        } else {
          setPerfectCount(null);
          setPerfectFetchError(true);
        }
      } catch (err) {
        if (!isMounted) return;
        setPerfectCount(null);
        setPerfectFetchError(true);
      } finally {
        if (isMounted) {
          setPerfectLoading(false);
        }
      }
    };

    setPerfectLoading(true);
    setPerfectFetchError(false);
    fetchPerfectCount();
    timer = setInterval(fetchPerfectCount, 60_000);

    return () => {
      isMounted = false;
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [router.isReady, examIdStr]);

  const pageTitle = `${examDetails?.name || "検定"} | 購入ページ`;

  if (currentUser === null) {
    return (
      <Layout layoutType={"YTLayout"}>
        <main css={createAccountLeadContainerStyle}>
          <CreateAccountLead
            label={"検定を受けるにはログインが必要です"}
            redirectPath={router.asPath}
          />
        </main>
      </Layout>
    );
  }

  const handlePurchase = async () => {
    try {
      if (!client || !examDetails) return;

      const response = await client.createExamPaymentLink({
        input: {
          examMakerId: examDetails.examMakerId,
          categoryId: parseInt(id as string, 10),
        },
      });

      const result = response?.createExamPaymentLink;
      const errors = result?.errors;

      if (errors?.length) {
        alert(errors.join("\n"));
        return;
      }

      // 無料検定の場合
      if (result?.isFree) {
        // 決済をスキップして直接受験ページへリダイレクト
        router.push(`/category/${id}/exam/${examId}/take`);
        return;
      }

      // 有料検定の場合
      if (result?.paymentLink?.url) {
        window.location.href = result.paymentLink.url;
      }
    } catch (err) {
      alert("処理中にエラーが発生しました");
    }
  };

  if (showNotFound) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <>
      <PageHead title={pageTitle} />
      <Layout layoutType={"YTLayout"}>
        <main css={rootStyle(theme)}>
          <div css={examHeaderStyle(theme)}>
            <h1>{examDetails?.name}</h1>
          </div>
          {isVerifyingPurchase ? (
            <div css={loadingContainerStyle}>
              <CircularProgress size={24} sx={{ mr: 1 }} />{" "}
              購入情報を確認しています...
            </div>
          ) : loading ? (
            <div css={loadingContainerStyle}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
            </div>
          ) : purchaseStatus === "questRequired" ? (
            <div css={questRequiredContainerStyle}>
              <div css={questRequiredMessageStyle}>
                この検定を受験するには、特定のクエストの実行が必要です。
              </div>
              <Button
                variant="contained"
                color="primary"
                onClick={() => router.push(`/category/${id}`)}
                css={questRequiredButtonStyle}
              >
                クエストを確認する
              </Button>
            </div>
          ) : error ? (
            <div css={errorContainerStyle}>{error}</div>
          ) : (
            <div css={examContentStyle}>
              <div css={examInfoContainerStyle}>
                <div css={examDescriptionStyle}>
                  <h2>検定概要</h2>
                  <div css={examDescriptionTextStyle}>
                    {examDetails?.description ?? ""}
                  </div>
                </div>

                <div css={examDetailsStyle}>
                  {examDetails?.level && (
                    <div css={detailItemStyle}>
                      <span>レベル</span>
                      <strong>{examDetails.level.toLocaleString()}級</strong>
                    </div>
                  )}
                  <div css={detailItemStyle}>
                    <span>合格点数</span>
                    <strong>
                      {(examDetails?.passingScore || 70).toLocaleString()}点
                    </strong>
                  </div>
                  <div css={detailItemStyle}>
                    <span>受験料</span>
                    <strong>
                      {examDetails?.isFree
                        ? "無料"
                        : `${(examDetails?.price || 2500).toLocaleString()}円`}
                    </strong>
                  </div>
                  {EXAMS_WITH_PERFECT_COUNT.includes(examIdStr) && (
                    <div css={perfectInfoDetailItemStyle(theme)}>
                      <div css={perfectInfoDetailLabelStyle(theme)}>
                        <span>現在の満点合格者数</span>
                        {perfectFetchError && (
                          <span css={perfectInfoDetailErrorStyle(theme)}>
                            取得できませんでした。時間をおいて再度アクセスしてください。
                          </span>
                        )}
                      </div>
                      <div css={perfectInfoDetailValueStyle(theme)}>
                        {perfectLoading ? (
                          <CircularProgress size={14} thickness={4.5} />
                        ) : perfectFetchError ||
                          typeof perfectCount !== "number" ? (
                          <span css={perfectInfoDetailPlaceholderStyle(theme)}>
                            --
                          </span>
                        ) : (
                          <>
                            <strong>{perfectCount}</strong>
                            <span>名</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div css={actionContainerStyle(theme)}>
                {/* 受験期間判定 */}
                {(() => {
                  const now = new Date();
                  const availableFrom = examDetails?.examAvailableFrom
                    ? new Date(examDetails.examAvailableFrom)
                    : null;
                  const availableTo = examDetails?.examAvailableTo
                    ? new Date(examDetails.examAvailableTo)
                    : null;

                  const isWithinExamPeriod =
                    (!availableFrom && !availableTo) ||
                    !availableFrom ||
                    !availableTo ||
                    (availableFrom <= now && now <= availableTo);

                  if (!isWithinExamPeriod) {
                    return (
                      <div css={errorContainerStyle}>
                        受験期間外です
                        {availableFrom && availableTo && (
                          <div>
                            （{availableFrom.toLocaleDateString("ja-JP")}{" "}
                            {availableFrom.toLocaleTimeString("ja-JP", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}{" "}
                            〜 {availableTo.toLocaleDateString("ja-JP")}{" "}
                            {availableTo.toLocaleTimeString("ja-JP", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            ）
                          </div>
                        )}
                      </div>
                    );
                  }

                  // 期間内の場合
                  if (purchaseStatus === "notPurchased") {
                    return (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handlePurchase}
                        css={actionButtonStyle}
                      >
                        {examDetails?.isFree ? "受験する" : "購入する"}
                      </Button>
                    );
                  }
                  if (purchaseStatus === "purchased") {
                    return (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() =>
                          router.push(`/category/${id}/exam/${examId}/take`)
                        }
                        css={actionButtonStyle}
                      >
                        受験する
                      </Button>
                    );
                  }
                  if (purchaseStatus === "taken") {
                    return (
                      <div css={takenMessageStyle(theme)}>
                        {examDetails?.isFree ? (
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() =>
                              router.push(`/category/${id}/exam/${examId}/take`)
                            }
                            css={actionButtonStyle}
                          >
                            再受験する
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={handlePurchase}
                            css={actionButtonStyle}
                          >
                            再購入する
                          </Button>
                        )}
                      </div>
                    );
                  }
                  if (purchaseStatus === null) {
                    return (
                      <div css={errorContainerStyle}>
                        ステータスを確認できませんでした
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}
        </main>
      </Layout>
    </>
  );
};

const createAccountLeadContainerStyle = css`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
`;

const rootStyle = (theme: Theme) => css`
  ${mediaQueryIsDesktop(css`
    max-width: 1000px;
    margin: 2rem auto;
    padding: 2rem;
    // background-color: #f8f8f8;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  `)};

  ${mediaQueryIsMobile(css`
    padding: 1rem;
    margin: 1rem;
  `)};
`;

const examHeaderStyle = (theme: Theme) => css`
  border-bottom: 2px solid ${theme.palette.primary.main};
  padding-bottom: 1rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;

  h1 {
    color: ${theme.palette.primary.main};
    margin: 0 0 0.5rem 0;
    font-size: 2rem;

    ${mediaQueryIsMobile(css`
      font-size: 1.5rem;
    `)};
  }
`;

const perfectInfoDetailItemStyle = (theme: Theme) => css`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 0.75rem;

  ${mediaQueryIsMobile(css`
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  `)}
`;

const perfectInfoDetailLabelStyle = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  span:first-of-type {
    font-size: 0.9rem;
    font-weight: 700;
    color: ${theme.palette.primary.main};
    letter-spacing: 0.04em;
  }
`;

const perfectInfoDetailErrorStyle = (theme: Theme) => css`
  font-size: 0.75rem;
  color: ${theme.palette.error.main};
  font-weight: 600;
`;

const perfectInfoDetailValueStyle = (theme: Theme) => css`
  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 0.3rem;
  min-width: 4.5rem;

  strong {
    font-size: 1.4rem;
    font-weight: 800;
    color: ${theme.palette.text.primary};
    line-height: 1;
  }

  span {
    font-size: 0.8rem;
    color: ${theme.palette.text.secondary};
  }

  ${mediaQueryIsMobile(css`
    justify-content: flex-start;
  `)}
`;

const perfectInfoDetailPlaceholderStyle = (theme: Theme) => css`
  font-size: 1.2rem;
  font-weight: 700;
  color: ${alpha(theme.palette.text.primary, 0.35)};
`;

const examDescriptionTextStyle = css`
  white-space: pre-line;
  line-height: 1.8;
  font-size: 0.95rem;
`;

const loadingContainerStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const errorContainerStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  color: #d32f2f;
`;

const examContentStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const examInfoContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  ${mediaQueryIsDesktop(css`
    flex-direction: row;
    align-items: flex-start;
  `)};
`;

const examDescriptionStyle = css`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  h2 {
    font-size: 1.5rem;
    margin-top: 0;
    margin-bottom: 1rem;
  }

  p {
    margin: 0;
    line-height: 1.7;
    word-break: break-word;
  }
`;

const examDetailsStyle = css`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  ${mediaQueryIsDesktop(css`
    min-width: 200px;
  `)};
`;

const detailItemStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #eee;

  span {
    color: #666;
  }

  strong {
    font-size: 1.1rem;
  }
`;

const actionContainerStyle = (theme: Theme) => css`
  display: flex;
  justify-content: center;
  margin: 2rem 0;
`;

const actionButtonStyle = css`
  min-width: 200px;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
`;

const errorMessageStyle = css`
  margin-bottom: 1rem;
  text-align: center;
`;

const questRequiredContainerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  margin: 4rem auto;
  max-width: 480px;
  text-align: center;
`;

const questRequiredMessageStyle = css`
  font-size: 1.3rem;
  line-height: 1.7;
  color: #333;
  text-align: center;
  margin-bottom: 2.5rem;
  font-weight: 500;
`;

const questRequiredButtonStyle = css`
  min-width: 220px;
  padding: 0.875rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 6px;
  background-color: #ff480f;
  color: white;
  border: 2px solid #ff480f;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e6400e;
    border-color: #e6400e;
    transform: translateY(-1px);
  }

  &:active {
    background-color: #cc390c;
    border-color: #cc390c;
    transform: translateY(0);
  }
`;

const takenMessageStyle = (theme: Theme) => css`
  text-align: center;

  p {
    margin-bottom: 1.5rem;
    color: ${theme.palette.warning.dark};
  }
`;

const tosContainerStyle = (theme: Theme) => css`
  margin-top: 3rem;
  display: flex;
  justify-content: end;
  padding-right: 1rem;

  a {
    color: ${theme.palette.primary.light};
    text-decoration: underline;
    font-size: 0.9rem;
  }
`;

export default ExamPurchasePage;

// URL整合性チェック用のgetServerSideProps
export async function getServerSideProps({
  params,
}: {
  params: { id: string; examId: string };
}) {
  const { validateExamUrl } = await import("~/utils/examUrlValidation");
  return await validateExamUrl(params.id, params.examId);
}
